import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import {
  BaseReportComponentProps,
  GroupBySecurityPerformance,
  HidingOptionSecurityPerformance,
  HidingOptionsRequest,
  MdaReportTypeField,
  MdaReportTypes,
  PerformanceMethodology,
} from '../../store/types';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface SecurityPerformanceProps extends BaseReportComponentProps {
  expandSecurityPerformance: ActionCreatorWithoutPayload<string>;
  setSecurityPerformanceSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeSecurityPerformanceParameter: ActionCreatorWithPayload<DateRange, string>;
  setSecurityPerformanceGroupByParameter: ActionCreatorWithPayload<GroupBySecurityPerformance, string>;
  setSecurityPerformanceMethodology: ActionCreatorWithPayload<PerformanceMethodology, string>;
  setSecurityPerformanceHidingOptions: ActionCreatorWithPayload<HidingOptionsRequest<HidingOptionSecurityPerformance>, string>;
}

export const SecurityPerformance = ({
  parameters,
  setDateRangeSecurityPerformanceParameter,
  setSecurityPerformanceSelectedParameter,
  setSecurityPerformanceGroupByParameter,
  setSecurityPerformanceHidingOptions,
  setSecurityPerformanceMethodology,
  expandSecurityPerformance,
  setGroupConsolidatedOption,
  isDatesLessThanFullYear,
  createReportSortRequest,
}: SecurityPerformanceProps): JSX.Element => {
  const handleClickSecurityPerformanceSelect = () => {
    setSecurityPerformanceSelectedParameter(!parameters.selectedReports?.securityPerformance.selected);
    createReportSortRequest(
      !parameters.selectedReports?.securityPerformance.selected,
      MdaReportTypes.SecurityPerformance.id,
      MdaReportTypes.SecurityPerformance.displayName
    );
  };

  const handleClickReportDateSecurityPerformance = (dates: DateRange) => {
    setDateRangeSecurityPerformanceParameter(dates);
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(dates)) {
      setSecurityPerformanceHidingOptions({ option: HidingOptionSecurityPerformance.AnnualisedReturns, value: false });
    }
  };

  useEffect(() => {
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(parameters.dates)) {
      setSecurityPerformanceHidingOptions({ option: HidingOptionSecurityPerformance.AnnualisedReturns, value: false });
    }
  }, [parameters.dates]);

  return (
    <Accordion onChange={expandSecurityPerformance} expanded={parameters.selectedReports?.securityPerformance.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.securityPerformance.selected}
              onChangeHandler={handleClickSecurityPerformanceSelect}
            />
          }
          label="Security Performance"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            dateRange={{
              dateFrom: parameters?.selectedReports.securityPerformance?.fromDate,
              dateTo: parameters?.selectedReports.securityPerformance?.toDate,
              dateRangeType: parameters?.selectedReports.securityPerformance?.dateRangeType,
            }}
            setDateRange={handleClickReportDateSecurityPerformance}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column">
          <Grid item container spacing={1} alignItems="center">
            <Grid item flex={1}>
              <FormControl fullWidth>
                <InputLabel id="group-by-id">Group by</InputLabel>
                <WO2Select
                  labelId="group-by-id"
                  label="group by"
                  onChange={(e) => setSecurityPerformanceGroupByParameter(e.target.value as GroupBySecurityPerformance)}
                  value={parameters.selectedReports.securityPerformance.groupBy}
                  defaultValue={GroupBySecurityPerformance.AssetClass}
                >
                  <MenuItem value={GroupBySecurityPerformance.AssetClass}>Asset Class</MenuItem>
                  <MenuItem value={GroupBySecurityPerformance.Gics}>GICS</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
            <Grid item flex={1}>
              <FormControl fullWidth>
                <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
                <WO2Select
                  labelId="performance-methodology"
                  label="performance methodology"
                  onChange={(e) => setSecurityPerformanceMethodology(e.target.value as PerformanceMethodology)}
                  value={parameters.selectedReports.securityPerformance.performanceMethodology}
                  defaultValue={PerformanceMethodology.TimeWeighted}
                >
                  <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
                  <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item container direction="row" alignItems="center" spacing={1} mt={1}>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.securityPerformance.excludeFrankingCredits}
                onChange={(e) =>
                  setSecurityPerformanceHidingOptions({
                    option: HidingOptionSecurityPerformance.FrankingCredits,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude Franking Credits</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.securityPerformance.showAnnualisedReturns}
                onChange={(e) =>
                  setSecurityPerformanceHidingOptions({
                    option: HidingOptionSecurityPerformance.AnnualisedReturns,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  isDatesLessThanFullYear &&
                  isDatesLessThanFullYear({
                    dateFrom: parameters?.selectedReports.securityPerformance?.fromDate,
                    dateTo: parameters?.selectedReports.securityPerformance?.toDate,
                    dateRangeType: parameters?.selectedReports.securityPerformance?.dateRangeType,
                  })
                }
              />
              <Typography variant="h4">Show Annualised Returns</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.securityPerformance.applyExclusionList}
                onChange={(e) =>
                  setSecurityPerformanceHidingOptions({
                    option: HidingOptionSecurityPerformance.ExclusionList,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.securityPerformance.excludeCapitalIncomeReturns}
                onChange={(e) => {
                  setSecurityPerformanceHidingOptions({
                    option: HidingOptionSecurityPerformance.CapitalIncomeReturn,
                    value: e.target.checked,
                  });

                  if (e.target.checked) {
                    setSecurityPerformanceHidingOptions({
                      option: HidingOptionSecurityPerformance.MarketFxReturn,
                      value: true,
                    });
                  }
                }}
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude Capital/Income Returns</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.securityPerformance.excludeMarketFxReturn}
                onChange={(e) =>
                  setSecurityPerformanceHidingOptions({
                    option: HidingOptionSecurityPerformance.MarketFxReturn,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={parameters.selectedReports.securityPerformance.excludeCapitalIncomeReturns}
              />
              <Typography variant="h4">Exclude Market/FX Return</Typography>
            </Grid>
            {setGroupConsolidatedOption && (
              <Grid item container spacing={4} direction="column" mt={1}>
                <Grid item>
                  <Divider />
                </Grid>
                <Grid item>
                  <ReportGroupingOption
                    value={parameters.selectedReports.securityPerformance.groupOption}
                    setGroupConsolidatedOption={setGroupConsolidatedOption}
                    report={MdaReportTypeField.SecurityPerformance}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
